// Client-facing labels + tiny shared renderers for the Autopilot and
// Timeline tabs. Copy rules: "strategy" (never proposal), "analysis" (never
// crawl), "product page" (never PDP URL).
import React from 'react';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { Verdict } from '../../../../types/autopilot';

export const ACTION_LABELS: Record<string, string> = {
  place_video: 'Place the expert video',
  remove_video: 'Remove the video',
  replace_video: 'Replace the video',
  reposition_video: 'Move the video up',
  update_video_poster: 'Update the video thumbnail',
  add_video_chapters: 'Add video chapters',
  add_embed_to_page: 'Add the player to the page',
  update_structured_data: 'Add the video markup',
  publish_transcript_or_captions: 'Publish the transcript',
  update_product_summary: 'Rewrite the product summary',
  update_benefit_bullets: 'Rewrite the benefit bullets',
  publish_faq: 'Publish an FAQ',
  publish_objection_answer: 'Answer the objection on the page',
  update_specifications: 'Update the specifications',
  publish_suitability_guide: 'Publish a "who it\'s for" guide',
  publish_usage_guide: 'Publish a usage guide',
  add_product_image: 'Add a product image',
  reorder_product_gallery: 'Reorder the gallery',
  publish_expert_card: 'Publish the expert card',
  publish_evidence_block: 'Publish the evidence block',
  publish_comparison: 'Publish a comparison',
  reposition_content_block: 'Move a content block up',
  remove_content_block: 'Remove a content block',
  produce_video: 'Produce an expert video',
  install_tag: 'Install the script tag',
  install_pixel: 'Install the Shopify pixel',
};

// Change events (what detection saw), by change_kind; the legacy type map
// stays for rows without one.
export const CHANGE_KIND_LABELS: Record<string, string> = {
  video_added: 'Video added',
  video_removed: 'Video removed',
  video_replaced: 'Video replaced',
  video_repositioned: 'Video moved',
  poster_changed: 'Thumbnail changed',
  copy_changed: 'Copy changed',
  markup_changed: 'Markup updated',
  embed_added: 'Player added',
  image_added: 'Image added',
  image_removed: 'Image removed',
  other: 'Change',
};

export const INTERVENTION_TYPE_LABELS: Record<string, string> = {
  place_video: 'Video added',
  remove_video: 'Video removed',
  replace_video: 'Video replaced',
  reposition_video: 'Video moved',
  update_video_poster: 'Thumbnail changed',
  embed_added: 'Player added',
  install_tag: 'Script tag',
  json_ld_placement: 'Video markup updated',
  update_structured_data: 'Video markup updated',
  page_change: 'Copy changed',
};

export const EXECUTOR_LABELS: Record<string, string> = {
  client: 'By you',
  tdc: 'Automatic',
  unknown: '',
};

export const DETECTION_SOURCE_LABELS: Record<string, string> = {
  analysis: 'seen by the analysis',
  probe: 'seen by a page check',
  tag: 'seen by the script tag',
  embed: 'seen by the player',
  manual: 'logged by you',
};

export const OBSERVATION_SOURCE_LABELS: Record<string, string> = {
  analysis: 'Analysis',
  probe: 'Page check',
  tag: 'Script tag',
  embed: 'Player',
  backfill: 'Earlier analysis',
};

export const RECOMMENDATION_STATUS_LABELS: Record<string, string> = {
  proposed: 'Proposed',
  accepted: 'Waiting for you',
  fulfilled: 'Done',
  expired: 'Expired',
  rejected: 'Dismissed',
  in_strategy: 'In a strategy',
  in_production: 'In production',
  superseded: 'Superseded',
};

export const INTERVENTION_SOURCE_LABELS: Record<string, string> = {
  recommendation: 'Recommendation',
  automatic: 'Automatic',
  detected: 'Detected',
  brand_reported: 'Logged by you',
};

export const INTERVENTION_STATUS_LABELS: Record<string, string> = {
  planned: 'Planned',
  ready: 'Ready',
  activating: 'Applying',
  active: 'Measuring',
  suspected: 'Suspected',
  retracted: 'Retracted',
  verification_failed: 'Not found on the page',
  paused: 'Paused',
  removed: 'Removed',
  completed: 'Measured',
};

export const LEARNING_STATUS_LABELS: Record<string, string> = {
  pending: 'Learning: waiting for data',
  eligible: 'Learning: measured',
  eligible_low_confidence: 'Learning: measured (low confidence)',
  confounded: 'Learning: confounded',
  insufficient_data: 'Learning: not enough data',
  incompatible_measurement: 'Learning: not measurable',
  invalid_tracking: 'Learning: tracking invalid',
};

export const OPPORTUNITY_KIND_LABELS: Record<string, string> = {
  tag_not_installed: 'Script tag not installed',
  markup_via_feed: 'Retailer page: checked by page checks',
  third_party_video_only: 'Only a non-expert video on the page',
  no_product_video: 'No expert video for this product',
  no_tdc_video_on_page: 'No expert video on the page',
  low_engagement: 'Video seen but rarely played',
  page_not_tracked: 'Page not tracked by the tag',
  video_not_seen: 'Video never shown to visitors',
  low_watch_depth: 'Viewers stop watching early',
  low_add_to_cart: 'Traffic without add-to-carts',
  'cv_score_weak:cv_trust_score': 'Shoppers doubt the product',
  'cv_score_weak:cv_education_score': 'Shoppers have unanswered questions',
  'cv_score_weak:cv_hype_score': 'Low shopper excitement',
};

// "cv_opportunity:<key>" -> "Consumer Voice: <key words>".
export const opportunityKindLabel = (kind: string | null | undefined): string => {
  if (!kind) return '—';
  if (OPPORTUNITY_KIND_LABELS[kind]) return OPPORTUNITY_KIND_LABELS[kind];
  // The key is a topic slug; the opportunity's problem text carries the
  // readable ask, shown under the label.
  if (kind.startsWith('cv_opportunity:')) return 'Shoppers ask about';
  // Findings → solves (one card per fix the analyses agree on).
  if (kind.startsWith('solve:')) return humanize(kind.slice('solve:'.length), ACTION_LABELS);
  if (kind.startsWith('cv_score_weak:')) {
    return `Weak ${humanize(kind.slice('cv_score_weak:'.length), METRIC_LABELS)} score`;
  }
  return humanize(kind, OPPORTUNITY_KIND_LABELS);
};

export const METRIC_LABELS: Record<string, string> = {
  pdp_overall: 'PDP score',
  pdp_human: 'Human Score',
  pdp_agentic: 'AI Score',
  cv_trust_score: 'Trust',
  cv_education_score: 'Education',
  cv_hype_score: 'Hype',
  cv_sentiment: 'Sentiment',
  cv_advocacy: 'Advocacy',
  cv_objection_severity: 'Objection severity',
  cv_demand_index: 'Demand',
  cv_opportunity_index: 'Opportunity',
  cv_retailer_rating: 'Retailer rating',
  returns_rate: 'Returns rate',
  'cv_opportunity:objection': 'Objections',
  'cv_opportunity:unanswered_question': 'Unanswered questions',
  'cv_opportunity:comparison': 'Comparison asks',
  'cv_opportunity:question_query': 'Question searches',
  'cv_opportunity:problem_query': 'Problem searches',
  'cv_opportunity:rising_query': 'Rising searches',
  'cv_opportunity:pdp_gap': 'Page gaps',
  aip_overall: 'AI Presence',
  aip_discovery: 'AI discovery',
  aip_recommendation: 'AI recommendation',
  aip_mrr: 'AI rank',
  aip_pdp_cited: 'Page cited by AI',
  tag_pageviews: 'Page views',
  tag_impressions: 'Video impressions',
  tag_plays: 'Plays',
  tag_watch_seconds: 'Watch time (s)',
  tag_atc_rate: 'Add-to-cart rate',
  tag_conversion_rate: 'Conversion rate',
  sc_impressions: 'Search impressions',
  sc_clicks: 'Search clicks',
  sc_position: 'Search position',
};

// The audit sections (pdp_section:<key>) as the report names them.
export const SECTION_LABELS: Record<string, string> = {
  conversionOptimization: 'Conversion',
  contentQuality: 'Content quality',
  visualExperience: 'Visual experience',
  socialProof: 'Social proof',
  seoMetadata: 'SEO',
  technicalPerformance: 'Technical',
  mobileFirst: 'Mobile',
  competitorBenchmarking: 'Competitor benchmark',
  structuredTruth: 'Structured data',
  semanticClarity: 'Semantic clarity',
  explainability: 'Explainability',
  multimodalGrounding: 'Multimodal grounding',
  executionConfidenceSignals: 'Execution confidence',
  machineCommerceSignals: 'Machine commerce',
};

export const metricLabel = (key: string | null | undefined): string => {
  if (!key) return '—';
  if (key.startsWith('pdp_section:')) return humanize(key.slice('pdp_section:'.length), SECTION_LABELS);
  return humanize(key, METRIC_LABELS);
};

// The prediction pill on a recommendation card: which metric, then the
// model's answer. A metric with no baseline yet (Trust before a Consumer
// Voice analysis) gets told so instead of a zero prediction.
export const predictionHeadline = (p: { metric_key: string; availability?: string | null; label: string } | null | undefined): string => {
  if (!p) return 'No prediction';
  const label = metricLabel(p.metric_key);
  if (p.availability === 'unavailable') return `${label} · nothing to predict against yet`;
  return `${label} · ${p.label.replace(/^Predicted /, 'predicted ')}`;
};

// Percent-like rates keep a decimal; scores and counts are whole numbers.
export const fmtMetricValue = (key: string, value: number | null | undefined): string => {
  if (value == null) return '—';
  if (key === 'aip_mrr') return fmtNum(value, 2);
  if (key.endsWith('_rate') || key === 'sc_position') return `${fmtNum(value, 1)}${key.endsWith('_rate') ? '%' : ''}`;
  return fmtNum(value, 0);
};

export const WINDOW_LABELS: Record<string, string> = {
  early: 'Early window',
  standard: 'Standard window',
  stabilization: 'Stabilization window',
};

// Known tokens get human copy; anything else prints as a readable version
// of the identifier so a new server-side key never renders blank.
export const humanize = (token: string | null | undefined, map: Record<string, string>): string => {
  if (!token) return '—';
  if (map[token]) return map[token];
  return token.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
};

export const fmtDate = (at?: string | null): string => (at ? dayjs(at).format('D MMM YYYY') : '—');

export const fmtNum = (n: number | null | undefined, digits = 0): string =>
  n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toFixed(digits).replace(/\.0+$/, '');

export const fmtDelta = (n: number | null | undefined): string => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const s = fmtNum(Math.abs(v), 1);
  if (v > 0) return `+${s}`;
  if (v < 0) return `-${s}`;
  return '0';
};

export const verdictGlyph = (verdict?: Verdict | string | null): string => {
  if (verdict === 'up') return '↑';
  if (verdict === 'down') return '↓';
  if (verdict === 'flat') return '→';
  return '';
};

export const verdictCls = (verdict?: Verdict | string | null): string => {
  if (verdict === 'up') return 'text-up';
  if (verdict === 'down') return 'text-down';
  return 'text-muted';
};

export function VerdictArrow({ verdict, className }: { verdict?: Verdict | string | null; className?: string }) {
  const glyph = verdictGlyph(verdict);
  if (!glyph) return null;
  const title = { up: 'Moved up', down: 'Moved down', flat: 'No change' }[verdict as 'up' | 'down' | 'flat'];
  return (
    <span className={classNames('font-bold', verdictCls(verdict), className)} title={title} aria-label={title}>
      {glyph}
    </span>
  );
}

export function Pill({
  children,
  tone = 'neutral',
  title,
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'accent';
  title?: string;
}) {
  const toneCls = {
    neutral: 'text-text-2 border-border-token',
    good: 'text-up border-up/40',
    warn: 'text-accent-text border-accent/40',
    bad: 'text-down border-down/40',
    accent: 'text-accent-text bg-tint border-transparent',
  }[tone];
  return (
    <span
      className={classNames(
        'inline-flex items-center gap-1 text-[11px] font-semibold border rounded-[6px] px-2 py-[2px] whitespace-nowrap',
        toneCls,
      )}
      title={title}
    >
      {children}
    </span>
  );
}

export const statusTone = (status: string): 'neutral' | 'good' | 'warn' | 'bad' | 'accent' => {
  if (status === 'active' || status === 'completed') return 'good';
  if (status === 'verification_failed') return 'bad';
  if (status === 'paused' || status === 'removed' || status === 'retracted') return 'neutral';
  if (status === 'activating' || status === 'ready' || status === 'suspected') return 'warn';
  return 'neutral';
};

// "61 → 66 (+5 ± 4, 3 analyses)" — the measurement with its uncertainty
// when the API carries it (se / n_post), else the plain delta.
export const fmtMeasurement = (m: { baseline?: number | null; value?: number | null; delta?: number | null; se?: number | null; n_base?: number | null; n_post?: number | null } | null | undefined): string => {
  if (!m) return '';
  const base = fmtNum(m.baseline, 1);
  const val = fmtNum(m.value, 1);
  let out = `${base} → ${val} (${fmtDelta(m.delta)}`;
  if (m.se != null) out += ` ± ${fmtNum(m.se, 1)}`;
  out += ')';
  if (m.n_post != null || m.n_base != null) {
    const n = m.n_post != null ? m.n_post : m.n_base;
    out += ` · ${n} analys${n === 1 ? 'is' : 'es'}`;
  }
  return out;
};

export const btnPrimary =
  'text-[12px] font-semibold text-white bg-primary rounded-md px-3 py-1.5 cursor-pointer hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed';
export const btnSecondary =
  'text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-3 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed';
export const btnGhost =
  'text-[12px] font-semibold text-muted hover:text-text-2 underline underline-offset-2 bg-transparent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed';
